import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Award, CalendarCheck } from "lucide-react";
import { IMAGES, SITE } from "@/constants/site";
import { KineticLines } from "@/components/KineticLines";
import { useLang } from "@/i18n/LangContext";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const { t, l } = useLang();
  const h = t.home.hero;

  return (
    <section ref={ref} className="relative overflow-hidden px-5 md:px-10 pt-40 md:pt-48 pb-20 md:pb-28" data-testid="hero">
      <div className="absolute -top-40 -right-40 w-[34rem] h-[34rem] rounded-full bg-terracotta/20 blur-3xl" aria-hidden="true" />
      <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[1.15fr_1fr] lg:gap-10">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2.5 rounded-full border border-terracotta/40 bg-white/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-terracotta-deep"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-terracotta" aria-hidden="true" />
            {h.badge}
          </motion.p>

          <h1 className="mt-8 font-display text-4xl sm:text-5xl lg:text-[4.2rem] font-extrabold leading-[1.04] tracking-tighter text-balance">
            <KineticLines
              lines={[
                <>{h.h1.l1} </>,
                <>{h.h1.l2} </>,
                <>
                  <span className="relative inline-block">
                    {h.h1.l3a}
                    <motion.span
                      className="absolute left-0 -bottom-1 h-[0.14em] w-full rounded-full bg-terracotta"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                      style={{ transformOrigin: "left" }}
                      aria-hidden="true"
                    />
                  </span>{" "}
                  {h.h1.l3b}
                </>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-plum/85"
          >
            {h.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={SITE.calendly}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-rdv"
              className="group inline-flex items-center gap-2.5 rounded-full bg-ink px-7 py-4 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-plum"
            >
              <CalendarCheck className="w-5 h-5 text-terracotta" />
              {h.ctaPrimary}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <Link
              to={l("/services")}
              data-testid="hero-cta-services"
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white/60 px-7 py-4 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-white"
            >
              {h.ctaSecondary}
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-8"
          >
            <p className="inline-flex items-center gap-2.5 rounded-full bg-terracotta/15 px-4 py-2 text-xs font-semibold text-terracotta-deep" data-testid="hero-experience-note">
              <Award className="w-4 h-4" aria-hidden="true" />
              {h.note}
            </p>
            <p className="mt-4 text-xs font-medium uppercase tracking-[0.16em] text-plum/50">
              {t.zones}
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:block"
        >
          <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-[2.5rem] shadow-[0_40px_80px_-20px_rgba(17,14,13,0.25)]">
            <img
              src={IMAGES.heroAbstract}
              alt={h.imageAlt}
              className="aspect-[4/3] w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/25 to-transparent" aria-hidden="true" />
          </motion.div>
          <motion.div style={{ y: cardY }} className="absolute -left-10 bottom-2 space-y-3">
            {h.badges.map((b, i) => (
              <motion.span
                key={b}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 1 + i * 0.15 }}
                className="flex w-max items-center gap-2 rounded-2xl border border-ink/5 bg-white/90 px-5 py-3 text-sm font-semibold shadow-[0_16px_40px_-12px_rgba(17,14,13,0.2)] backdrop-blur"
              >
                <span className="h-2 w-2 rounded-full bg-terracotta" aria-hidden="true" />
                {b}
              </motion.span>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="mx-auto mt-16 flex max-w-7xl items-center gap-3 text-xs font-medium uppercase tracking-[0.2em] text-plum/40"
        aria-hidden="true"
      >
        <ArrowDown className="w-4 h-4 animate-bounce" />
        {h.scroll}
      </motion.div>
    </section>
  );
};
